// Gestión de Roles — perfiles asignados a Roles (no a usuarios).
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, X, Lock, Trash2, Pencil, Layers, AlertCircle, CheckCircle2, Sparkles,
} from "lucide-react";

const BASE_ROLES = [
  { key: "admin",        label: "Panel Administrador", color: "#1F2547", description: "Acceso a Franquicias, Usuarios, Auditoría, Roles y Diagnóstico global." },
  { key: "franquiciado", label: "Panel Franquiciado",  color: "#F5B82E", description: "Gestión interna de la franquicia: usuarios, asignaciones, resultados." },
  { key: "docente",      label: "Panel Docente",       color: "#2C49A0", description: "Mis estudiantes, agendar diagnósticos, diagnósticos académicos." },
  { key: "estudiante",   label: "Panel Estudiante",    color: "#D63384", description: "Mis diagnósticos, ruta de aprendizaje, perfil." },
];

export default function AdminRoles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [roles, setRoles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/roles"),
        api.get("/permission-profiles"),
      ]);
      setRoles(r.data);
      setProfiles(p.data);
    } catch (e) {
      toast.error("No se pudieron cargar los roles");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const profileById = (id) => profiles.find((p) => p.id === id);

  const onDelete = async (role) => {
    if (!confirm(`¿Eliminar el rol "${role.name}"?`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Rol eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Accesibilidad</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Roles</h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Cada rol tiene un perfil de permisos asignado. Al crear un usuario sólo eliges el rol; los módulos se aplican automáticamente.
          </p>
        </div>
        <button
          data-testid="open-create-role"
          onClick={() => setShowCreate(true)}
          className="jasq-btn-primary inline-flex items-center gap-2"
        >
          <Plus size={16} /> Crear rol personalizado
        </button>
      </div>

      <div className="jasq-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-[#E1E5F0] bg-[#F7F8FA]">
              {["Rol", "Tipo", "Panel del sistema", "Perfil de permisos", "Estado", "Acciones"].map((h) => (
                <th key={h} className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="p-8 text-center text-sm text-[#6E7591]">Cargando…</td></tr>)}
            {!loading && roles.map((r) => {
              const prof = profileById(r.profile_id);
              const isActive = r.active !== false;
              return (
                <tr key={r.id} data-testid={`role-row-${r.id}`} className="border-b border-[#E1E5F0] last:border-0 hover:bg-[#FAFBFE]">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${r.color || "#A43790"}22`, color: r.color || "#A43790" }}>
                        <ShieldCheck size={14} />
                      </span>
                      <div>
                        <p className="text-sm font-extrabold text-[#1F2547]">{r.name}</p>
                        <p className="text-[11px] text-[#6E7591] font-mono">{r.key}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {r.is_system ? (
                      <span className="jasq-pill bg-[#E8EEFB] text-[#2C49A0]"><Lock size={11} /> Sistema</span>
                    ) : (
                      <span className="jasq-pill bg-[#FFF7DA] text-[#9C7A18]">Personalizado</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-[#4C5478] font-bold">
                    {(BASE_ROLES.find((b) => b.key === r.base_role)?.label) || `Panel ${r.base_role}`}
                  </td>
                  <td className="px-5 py-3">
                    {prof ? (
                      <div data-testid={`role-profile-${r.id}`}>
                        <span className="inline-flex items-center gap-2 text-sm font-bold text-[#1F2547]">
                          <span className="w-2 h-2 rounded-full" style={{ background: prof.color || "#F5B82E" }} />
                          {prof.name}
                        </span>
                        <p className="text-[10px] text-[#3FA84D] font-bold mt-1 inline-flex items-center gap-1">
                          <CheckCircle2 size={10} /> {prof.modules.length} módulos activos
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-[#6E7591] italic" data-testid={`role-profile-${r.id}`}>
                        Sin perfil (defaults del rol)
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      data-testid={`role-status-${r.id}`}
                      className={`jasq-pill text-xs font-bold ${isActive ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFE9F0] text-[#A52258]"}`}
                    >
                      {isActive ? "Activo" : "Suspendido"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1.5">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`edit-role-${r.id}`}
                        className="w-9 h-9 rounded-lg bg-[#FFF7DA] hover:bg-[#FFCC28] hover:text-[#1F2547] flex items-center justify-center text-[#9C7A18] transition-colors"
                        title={r.is_system ? "Editar (nombre, color, descripción y perfil)" : "Editar rol"}
                      ><Pencil size={14} /></button>
                      {!r.is_system && isAdmin && (
                        <button
                          onClick={() => onDelete(r)}
                          data-testid={`delete-role-${r.id}`}
                          className="w-9 h-9 rounded-lg bg-[#FFE9F0] hover:bg-[#D43790] hover:text-white flex items-center justify-center text-[#A52258] transition-colors"
                          title="Eliminar"
                        ><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateRoleModal
          profiles={profiles}
          onClose={() => setShowCreate(false)}
          onDone={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && (
        <EditRoleModal
          role={editing}
          profiles={profiles}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateRoleModal({ profiles, onClose, onDone }) {
  const [form, setForm] = useState({
    key: "",
    name: "",
    description: "",
    base_role: "docente",
    profile_id: "",
    color: "#A43790",
    active: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/roles", {
        ...form,
        profile_id: form.profile_id || null,
      });
      toast.success("Rol creado");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="create-role-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Accesibilidad</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Nuevo rol personalizado</h3>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="rounded-xl border-2 border-[#E1E5F0] bg-[#F7F8FA] p-3 text-xs text-[#4C5478] flex gap-2">
            <Sparkles size={14} className="text-[#A43790] shrink-0 mt-0.5" />
            <span>
              Estás creando <b>un rol nuevo</b>. Solo necesitas indicar en qué <b>panel del sistema</b> trabajará (Administrador, Franquiciado, Docente o Estudiante) — esto define los menús y dashboards que verá el usuario al ingresar. Los <b>permisos finos</b> se controlan luego con el "Perfil de permisos".
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key (única, sin espacios)">
              <input data-testid="role-key" value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.replace(/[^a-z0-9_-]/gi, "").toLowerCase() })}
                placeholder="coord-pedagogico"
                className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-mono text-sm" />
            </Field>
            <Field label="Nombre visible">
              <input data-testid="role-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Coordinador Pedagógico"
                className="w-full px-3 py-2 rounded-xl border-2 border-[#A43790] focus:border-[#A43790] font-medium" />
            </Field>
          </div>
          <Field label="¿En qué panel del sistema operará este rol?">
            <div className="grid grid-cols-2 gap-2">
              {BASE_ROLES.map((b) => {
                const active = form.base_role === b.key;
                return (
                  <button key={b.key} type="button" data-testid={`role-base-${b.key}`}
                    onClick={() => setForm({ ...form, base_role: b.key })}
                    style={active ? { borderColor: b.color, background: b.color } : {}}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${active ? "text-white" : "border-[#E1E5F0] hover:border-[#A43790]"}`}>
                    <p className="text-sm font-bold leading-tight">{b.label}</p>
                    <p className={`text-[10px] leading-tight mt-1 ${active ? "text-white/80" : "text-[#6E7591]"}`}>{b.description}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6E7591] mt-1.5">
              El panel define los menús, dashboards y rutas que verá el usuario. Los módulos accesibles se afinan después con el perfil de permisos.
            </p>
          </Field>
          <Field label="Perfil de permisos (módulos accesibles)">
            <select data-testid="role-profile-id" value={form.profile_id}
              onChange={(e) => setForm({ ...form, profile_id: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-medium text-sm bg-white">
              <option value="">— Usar permisos por defecto del panel —</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.modules.length} mods</option>
              ))}
            </select>
          </Field>
          <Field label="Descripción">
            <textarea data-testid="role-desc" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Ej: Coordinador encargado de…"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-medium text-sm" />
          </Field>
          <Field label="Estado inicial del rol">
            <div className="flex gap-2">
              {[{ k: true, l: "Activo" }, { k: false, l: "Suspendido" }].map((s) => (
                <button key={s.l} type="button"
                  data-testid={`role-active-${s.k ? "on" : "off"}`}
                  onClick={() => setForm({ ...form, active: s.k })}
                  className={`flex-1 px-3 py-2 rounded-xl border-2 text-sm font-bold transition-all ${form.active === s.k ? (s.k ? "border-[#2A7A36] bg-[#E7F4E9] text-[#2A7A36]" : "border-[#A52258] bg-[#FFE9F0] text-[#A52258]") : "border-[#E1E5F0] hover:border-[#F5B82E]"}`}>
                  {s.l}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#6E7591] mt-1.5">
              Los roles <b>suspendidos</b> siguen existiendo pero no permiten crear nuevos usuarios con ese rol ni iniciar sesión a los ya asignados.
            </p>
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={saving || !form.key || !form.name} className="jasq-btn-primary"
            data-testid="role-submit">
            {saving ? "Guardando…" : "Crear rol"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRoleModal({ role, profiles, onClose, onDone }) {
  const [form, setForm] = useState({
    name: role.name || "",
    description: role.description || "",
    profile_id: role.profile_id || "",
    color: role.color || "#A43790",
    active: role.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/roles/${role.id}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        profile_id: form.profile_id || null,
        color: form.color,
        active: form.active,
      });
      toast.success(role.is_system ? "Rol del sistema actualizado" : "Rol actualizado");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const PRESET_COLORS = ["#1F2547", "#2C49A0", "#A43790", "#D63384", "#F5B82E", "#3FA84D", "#315197", "#9C7A18"];

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="edit-role-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${form.color}22`, color: form.color }}>
              <ShieldCheck size={18} />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">
                {role.is_system ? "Editar rol del sistema" : "Editar rol"}
              </p>
              <h3 className="font-display text-xl font-extrabold text-[#1F2547] truncate">{role.name}</h3>
              <p className="text-[11px] text-[#6E7591] font-mono">{role.key} · {BASE_ROLES.find((b) => b.key === role.base_role)?.label || role.base_role}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="edit-role-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {role.is_system && (
            <div className="rounded-xl border-2 border-[#E1E5F0] bg-[#F7F8FA] p-3 text-xs text-[#4C5478] inline-flex items-center gap-2">
              <Lock size={12} className="text-[#A43790]" />
              <span>Este es un rol del sistema. Puedes editar su nombre, color, descripción y perfil; <b>no</b> su key ni su rol base.</span>
            </div>
          )}
          <Field label="Nombre visible">
            <input data-testid="edit-role-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-medium" />
          </Field>
          <Field label="Color de la insignia">
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button key={c} type="button" data-testid={`edit-role-color-${c.slice(1)}`}
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-9 h-9 rounded-xl border-2 ${form.color === c ? "border-[#1F2547] scale-110" : "border-[#E1E5F0]"} transition-transform`}
                  style={{ background: c }}
                  title={c}
                />
              ))}
            </div>
          </Field>
          <Field label="Perfil de permisos">
            <select data-testid="edit-role-profile" value={form.profile_id}
              onChange={(e) => setForm({ ...form, profile_id: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-medium text-sm bg-white">
              <option value="">— Defaults del rol —</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {p.modules.length} mods</option>
              ))}
            </select>
          </Field>
          <Field label="Descripción">
            <textarea data-testid="edit-role-desc" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-medium text-sm" />
          </Field>
          {!role.is_system && (
            <Field label="Estado">
              <div className="flex gap-2">
                {[{ k: true, l: "Activo" }, { k: false, l: "Suspendido" }].map((s) => (
                  <button key={s.l} type="button"
                    data-testid={`edit-role-active-${s.k ? "on" : "off"}`}
                    onClick={() => setForm({ ...form, active: s.k })}
                    className={`flex-1 px-3 py-2 rounded-xl border-2 text-sm font-bold ${form.active === s.k ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#F5B82E]"}`}>
                    {s.l}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={saving || !form.name.trim()} className="jasq-btn-primary"
            data-testid="edit-role-submit">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
